import {
  calculateTicketedBookingTotal,
  calculateWidgetBookingTotal,
  getAdjustedRates,
  getFixedCardFeeShares,
  getMultiDayBooking,
  getProgressivePricing,
  getTicketRates
} from "utils/widgetBookingPricing"
import { getBookableTicketTypes } from "../helpers"

const FEE_KINDS = ["fee", "tax_and_fees", "card_processing_fee"]
const EXTRA_GUEST_KINDS = ["extra_adult", "extra_child", "extra_infant"]
// Rounding noise below a cent is not a saving.
const MIN_SAVING = 0.005

// Everything that moves a price off its base: day and seasonal adjustments, and a dynamic multi-day
// trip's progressive pricing. Left out for the price before adjustments.
const getAdjustmentSources = (experience) => ({
  dayAdjustments: experience?.day_adjustments,
  seasonalAdjustments: experience?.seasonal_adjustments,
  availableDateRanges: experience?.available_date_ranges,
  progressivePricing: getProgressivePricing(experience)
})

// Unit prices for `date` next to the prices before day/seasonal adjustments, for the struck-through
// "$96 $80" pairs on duration cards and guest rows. A multi-day trip's prices cover the whole trip
// (from `date` to `endDate` for a dynamic one).
export const getRateComparison = ({ experience, priceData, date, endDate }) => {
  const multiDay = getMultiDayBooking({ experience, priceData, date, endDate })
  return {
    rates: getAdjustedRates({ priceData, date, multiDay, ...getAdjustmentSources(experience) }),
    baseRates: getAdjustedRates({ priceData, date, multiDay })
  }
}

// The same pair for one day's rate on `date`, for a price sold per day shown "/ day".
export const getDayRateComparison = ({ experience, priceData, date }) => ({
  rates: getAdjustedRates({ priceData, date, ...getAdjustmentSources(experience) }),
  baseRates: getAdjustedRates({ priceData, date })
})

// Each ticket type's price for `date` next to its base price: { rates, baseRates }, keyed by
// ticket type id.
export const getTicketRateComparison = ({ experience, date }) => {
  const ticketTypes = getBookableTicketTypes(experience)
  return {
    rates: getTicketRates({ ticketTypes, date, ...getAdjustmentSources(experience) }),
    baseRates: getTicketRates({ ticketTypes, date })
  }
}

// Shows the original price only when the adjustment lowered it (a surcharge is not struck through).
export const getOriginalIfDiscounted = (original, adjusted) => (Number(original) - Number(adjusted) > MIN_SAVING ? Number(original) : null)

// The biggest saving any ticket type gets on `date`, as a whole percentage of its base price (20
// for "-20%"), or null when no ticket type is cheaper that day. A saving under 1% still shows as 1%.
const getTicketDiscountPercent = (experience, date) => {
  const { rates, baseRates } = getTicketRateComparison({ experience, date })
  const percents = Object.keys(baseRates)
    .filter((ticketTypeId) => Number(baseRates[ticketTypeId]) > 0 && getOriginalIfDiscounted(baseRates[ticketTypeId], rates[ticketTypeId]) != null)
    .map((ticketTypeId) => ((baseRates[ticketTypeId] - rates[ticketTypeId]) / baseRates[ticketTypeId]) * 100)
  return percents.length ? Math.min(100, Math.max(1, Math.round(Math.max(...percents)))) : null
}

// The calendar's "Price per person" for `date`: each bookable ticket type's price that day, with the
// price before the discount when it is lower (Figma 6058:138530).
export const getTicketDayPrices = (experience, date) => {
  const { rates, baseRates } = getTicketRateComparison({ experience, date })
  return getBookableTicketTypes(experience).map((ticketType) => ({
    id: ticketType.id,
    title: ticketType.title,
    price: Number(rates[ticketType.id]) || 0,
    originalPrice: getOriginalIfDiscounted(baseRates[ticketType.id], rates[ticketType.id])
  }))
}

// The price bar's "From $100 $80" (Figma 6058:142374): the cheapest ticket on any of `dates` (the
// bookable ones), with the cheapest ticket's usual price struck through when a discount makes it
// lower. Null when no ticket type has a price.
export const getTicketLowestPrice = (experience, dates = []) => {
  const ticketTypes = getBookableTicketTypes(experience)
  const basePrices = ticketTypes.map((ticketType) => Number(ticketType.price)).filter((price) => price > 0)
  if (!basePrices.length) return null
  const basePrice = Math.min(...basePrices)
  const dayPrices = dates.flatMap((date) => Object.values(getTicketRates({ ticketTypes, date, ...getAdjustmentSources(experience) })))
  const price = Math.min(basePrice, ...dayPrices.map(Number).filter((dayPrice) => dayPrice > 0))
  return { price, originalPrice: getOriginalIfDiscounted(basePrice, price) }
}

// Calendar markers: { "YYYY-MM-DD": percent } for the discounted days among `dates`.
export const getTicketDiscountsByDate = (experience, dates = []) =>
  Object.fromEntries(
    dates.map((date) => [date, getTicketDiscountPercent(experience, date)]).filter(([, percent]) => percent != null)
  )

// Breakdown lines grouped the way the widget words them: extra guests of every type become one
// "Extra Guests x N" line, add-ons keep their title, and every fee and tax adds up into one
// "Taxes & Fees" amount. Line keys are unique (tickets and add-ons by id) so they can key a list.
export const groupBreakdownLines = (breakdown = []) => {
  const lines = []
  let extraGuests = null
  let taxesAndFees = 0
  for (const line of breakdown) {
    if (FEE_KINDS.includes(line.kind)) {
      taxesAndFees += line.value
    } else if (EXTRA_GUEST_KINDS.includes(line.kind)) {
      if (!extraGuests) {
        extraGuests = { key: "extra_guests", kind: "extra_guests", count: 0, value: 0 }
        lines.push(extraGuests)
      }
      extraGuests.count += line.count
      extraGuests.value += line.value
    } else if (line.kind === "ticket") {
      lines.push({ key: `ticket_${line.ticketTypeId}`, kind: line.kind, count: line.count, value: line.value, title: line.title })
    } else if (line.kind === "add_on") {
      lines.push({ key: `add_on_${line.addOnId}`, kind: line.kind, count: line.count, value: line.value, title: line.title })
    } else {
      lines.push({ key: line.kind, kind: line.kind, count: line.count, value: line.value })
    }
  }
  return { lines, taxesAndFees }
}

// Panel lines, Taxes & Fees and the total, with the total before day/seasonal adjustments struck
// through when they lowered it.
const summarizePanelTotals = (adjusted, original) => {
  const { lines, taxesAndFees } = groupBreakdownLines(adjusted.breakdown)
  const originalTotal = getOriginalIfDiscounted(original.total, adjusted.total)
  return {
    lines,
    taxesAndFees,
    total: adjusted.total,
    originalTotal,
    savedAmount: originalTotal ? originalTotal - adjusted.total : 0
  }
}

// Booking panel summary. Guest lines are grouped the way the design words them ("Guests x 10",
// "Extra Guests x 2", "Adults x 4"), every fee and tax collapses into one "Taxes & Fees" line, and
// the total is compared with the same booking priced without day/seasonal adjustments. Priced as a
// full card payment, since the payment option is only chosen at checkout.
export const buildPanelSummary = ({ experience, priceData, date, endDate, counts }) => {
  const shared = {
    priceData,
    date,
    counts,
    multiDay: getMultiDayBooking({ experience, priceData, date, endDate }),
    customFees: experience?.assigned_custom_fees || [],
    cardFeePassthroughEnabled: experience?.platform_fee_setting?.card_fee_passthrough_enabled === true,
    depositFraction: 1
  }
  const adjusted = calculateWidgetBookingTotal({ ...shared, ...getAdjustmentSources(experience) })
  const original = calculateWidgetBookingTotal(shared)
  return summarizePanelTotals(adjusted, original)
}

// Ticketed booking panel summary: a line per ticket type ("Adults x 4"), then the same Taxes &
// Fees, total and saving as the private panel.
export const buildTicketedPanelSummary = ({ experience, date, ticketCounts }) => {
  const shared = {
    ticketTypes: getBookableTicketTypes(experience),
    ticketCounts,
    date,
    customFees: experience?.assigned_custom_fees || [],
    cardFeePassthroughEnabled: experience?.platform_fee_setting?.card_fee_passthrough_enabled === true,
    depositFraction: 1
  }
  const adjusted = calculateTicketedBookingTotal({ ...shared, ...getAdjustmentSources(experience) })
  const original = calculateTicketedBookingTotal(shared)
  return summarizePanelTotals(adjusted, original)
}

// Cart totals for one booking, split the way the cart shows them: the experience price (guests and
// their taxes, as on the experience page), the add-ons, the taxes & fees the add-ons add, and the
// total — each priced like the panel, with the undiscounted figures for the struck-through prices.
const summarizeCart = ({ calculate, experience, booking, addOnSelections, cardFeeShare = 1 }) => {
  const shared = {
    ...booking,
    tripAddOns: experience?.add_ons || [],
    customFees: experience?.assigned_custom_fees || [],
    cardFeePassthroughEnabled: experience?.platform_fee_setting?.card_fee_passthrough_enabled === true,
    depositFraction: 1,
    cardFeeShare
  }
  const adjustments = getAdjustmentSources(experience)
  const withAddOns = calculate({ ...shared, ...adjustments, addOnSelections })
  const experienceOnly = calculate({ ...shared, ...adjustments, addOnSelections: {} })
  const originalWithAddOns = calculate({ ...shared, addOnSelections })
  const originalExperienceOnly = calculate({ ...shared, addOnSelections: {} })

  return {
    experienceTotal: experienceOnly.total,
    originalExperienceTotal: getOriginalIfDiscounted(originalExperienceOnly.total, experienceOnly.total),
    addOnsTotal: withAddOns.addOnsTotal,
    addOnFees: Math.max(0, withAddOns.total - experienceOnly.total - withAddOns.addOnsTotal),
    total: withAddOns.total,
    originalTotal: getOriginalIfDiscounted(originalWithAddOns.total, withAddOns.total),
    preCardTotal: withAddOns.preCardTotal
  }
}

// Cart totals for a private booking.
export const buildCartSummary = ({ experience, priceData, date, endDate, counts, addOnSelections, cardFeeShare }) =>
  summarizeCart({
    calculate: calculateWidgetBookingTotal,
    experience,
    booking: { priceData, date, counts, multiDay: getMultiDayBooking({ experience, priceData, date, endDate }) },
    addOnSelections,
    cardFeeShare
  })

// Same cart totals for a ticketed booking.
export const buildTicketedCartSummary = ({ experience, date, ticketCounts, addOnSelections, cardFeeShare }) =>
  summarizeCart({
    calculate: calculateTicketedBookingTotal,
    experience,
    booking: { ticketTypes: getBookableTicketTypes(experience), ticketCounts, date },
    addOnSelections,
    cardFeeShare
  })

// One cart item's totals, private or ticketed.
export const buildCartItemSummary = ({ experience, item, cardFeeShare }) => {
  const addOnSelections = item.addOns || {}
  if (item.scheduledSlot) return buildTicketedCartSummary({ experience, date: item.date, ticketCounts: item.ticketCounts, addOnSelections, cardFeeShare })
  return buildCartSummary({
    experience,
    priceData: item.tripLength?.price || {},
    date: item.date,
    endDate: item.endDate,
    counts: item.counts,
    addOnSelections,
    cardFeeShare
  })
}

// Every cart item's totals ([{ experience, item }]), with the card fee's fixed part charged once for
// the cart (getFixedCardFeeShares).
export const buildCartItemSummaries = (entries) => {
  const summaries = entries.map(buildCartItemSummary)
  if (summaries.length < 2) return summaries
  const shares = getFixedCardFeeShares(summaries.map((summary) => summary.preCardTotal))
  return entries.map((entry, index) => buildCartItemSummary({ ...entry, cardFeeShare: shares[index] }))
}

// The cart's totals across its items. The total before day/seasonal adjustments is struck through
// when any item was discounted (an item without a discount counts at its own total).
export const combineCartSummaries = (summaries) => {
  const sum = (pick) => summaries.reduce((total, summary) => total + pick(summary), 0)
  const isDiscounted = summaries.some((summary) => summary.originalTotal != null)
  return {
    experienceTotal: sum((summary) => summary.experienceTotal),
    addOnsTotal: sum((summary) => summary.addOnsTotal),
    addOnFees: sum((summary) => summary.addOnFees),
    total: sum((summary) => summary.total),
    originalTotal: isDiscounted ? sum((summary) => summary.originalTotal ?? summary.total) : null
  }
}
